import { Policy } from "@p0security/shared/types/policy/types";
import {
  Drawer,
  Flex,
  Tag,
  Timeline,
  TimelineItemProps,
  Typography,
} from "@p0security/ui";
import {
  HeaderRow,
  RequestorDetails,
  SectionLabel,
  TitleText,
} from "components/Policies/common/DrawerPrimitives";
import { useMemo } from "react";

import { MaxDurationTag } from "../components/previews/ApprovalOptionsPreview";
import { ApprovalPreview } from "../components/previews/ApprovalPreview";
import { RequestorPreview } from "../components/previews/RequestorPreview";
import { ResourcePreview } from "../components/previews/ResourcePreview";

type Props = {
  rule: Policy | null;
  onClose: () => void;
};

const buildTimelineItems = (rule: Policy): TimelineItemProps[] => {
  const approvals = rule.approval ?? [];

  return [
    {
      color: "gray",
      children: (
        <Flex vertical gap={8}>
          <SectionLabel>Identities</SectionLabel>
          <RequestorPreview requestor={rule.requestor} />
          <RequestorDetails requestor={rule.requestor} />
        </Flex>
      ),
    },
    {
      color: "gray",
      children: (
        <Flex vertical gap={8}>
          <SectionLabel>Resources</SectionLabel>
          <ResourcePreview resource={rule.resource} />
        </Flex>
      ),
    },
    {
      color: "gray",
      children: (
        <Flex vertical gap={8}>
          <SectionLabel>Actions</SectionLabel>
          {approvals.length === 0 ? (
            <Typography.Text type="secondary">
              No actions configured
            </Typography.Text>
          ) : (
            <Flex vertical gap={8}>
              {approvals.map((approval, i) => (
                <Flex vertical gap={4} key={i}>
                  <ApprovalPreview approval={approval} />
                  <MaxDurationTag approval={approval} />
                </Flex>
              ))}
            </Flex>
          )}
        </Flex>
      ),
    },
  ];
};

export const RulePreviewDrawer: React.FC<Props> = ({ rule, onClose }) => {
  const timelineItems = useMemo(
    () => (rule ? buildTimelineItems(rule) : []),
    [rule]
  );

  const displayName = rule?.name?.trim() || "Untitled policy";
  const isPlaceholder = !rule?.name?.trim();

  const drawerTitle = (
    <HeaderRow>
      <TitleText isPlaceholder={isPlaceholder}>{displayName}</TitleText>
      {rule?.disabled && <Tag color="orange">Disabled</Tag>}
    </HeaderRow>
  );

  return (
    <Drawer
      title={drawerTitle}
      width={480}
      open={!!rule}
      onClose={onClose}
      destroyOnClose
    >
      {rule && <Timeline items={timelineItems} />}
    </Drawer>
  );
};
